import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useSelectionListKeyHandler } from "src/hooks/ticket/selection-list-key-handler/useSelectionListKeyHandler";
import { boldenMatchedSubstring } from "src/utils/Common";
import { IPayoutAccountData } from "src/stores/krediflow/InvoiceTypes";
import { ErpType } from "src/network/User";
import styles from "./PayoutAccountFieldIT2.module.css";
import { LOADING_PAYOUT_ACCOUNT_STATE } from "src/stores/krediflow/AccountingPayoutAccountStore";
import { friendlyFormatIBAN } from "ibantools";

const PayoutAccountIT2FieldBase = () => {
    const { t } = useTranslation();
    const { accountingVatStore, accountingPayoutAccountStore, invoiceStore, authStore } = useContext(RootStoreContext);

    const [displayPayoutAccountList, setDisplayPayoutAccountList] = useState<boolean>(false);
    const [lastScrollPositionYPayoutAccountItems, setLastSrollYPositionPayoutAccountItems] = useState<number>(0);

    const payoutAccountInputRef = useRef<HTMLInputElement>(null);

    const updateAccountingWithSelectedPayoutAccount = useCallback(
        async (payoutAccountData: IPayoutAccountData) => {
            invoiceStore.updateInvoiceWithSelectedPayoutAccountIT2(payoutAccountData);
        },
        [invoiceStore]
    );

    const resetSearchQuery = useCallback(() => {
        accountingPayoutAccountStore.setCurrentPayoutAccountSearchQueryIT2(
            accountingPayoutAccountStore.selectedPayoutAccountQueryStringIT2 ?? ""
        );
    }, [accountingPayoutAccountStore]);

    useEffect(() => {
        const handleClickOutsidePayoutAccountInput = (event: any) => {
            const target = event.target as HTMLElement;

            const isClickOutsidePayoutAccountInputOrList = () => {
                if (!displayPayoutAccountList || !payoutAccountInputRef.current) {
                    return true;
                }

                const clickedInsideInput = payoutAccountInputRef.current.contains(target);

                const clickedListItem =
                    target.classList.contains("list-item") && target.classList.contains("accounting-payoutaccount");

                const clickedListItemFormattedText = target.classList.contains("accounting-payoutaccount-text");

                const clickedIcon = target.localName === "path" || target.localName === "svg";

                const clickedHighlightedText =
                    target.classList.contains("highlighted-text") &&
                    target.classList.contains("accounting-payoutaccount");

                const clickedListItemChildElement =
                    target.parentElement?.classList.contains("list-item") &&
                    target.parentElement.classList.contains("accounting-payoutaccount");

                return !(
                    clickedInsideInput ||
                    clickedListItem ||
                    clickedListItemFormattedText ||
                    clickedListItemChildElement ||
                    clickedIcon ||
                    clickedHighlightedText
                );
            };

            if (isClickOutsidePayoutAccountInputOrList()) {
                setDisplayPayoutAccountList(false);

                if (
                    invoiceStore.currentInvoice?.isPayoutAccountIdSelected &&
                    accountingPayoutAccountStore.currentPayoutAccountSearchQueryIT2 === ""
                ) {
                    invoiceStore.resetPayoutAccountFormFields();
                } else {
                    resetSearchQuery();
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutsidePayoutAccountInput);

        return () => {
            document.removeEventListener("mousedown", handleClickOutsidePayoutAccountInput);
        };
    }, [
        displayPayoutAccountList,
        resetSearchQuery,
        accountingVatStore,
        invoiceStore.currentInvoice?.isAccountingVatRateSelected,
        invoiceStore,
        accountingPayoutAccountStore.currentPayoutAccountSearchQueryIT2,
        accountingPayoutAccountStore.selectedPayoutAccountQueryStringIT2
    ]);

    const onSelectPayoutAccountItem = useCallback(
        (activeListItemIndex: number) => {
            const selectedPayoutAccountItem =
                accountingPayoutAccountStore.selectablePayoutAccountDataIT2[activeListItemIndex];

            updateAccountingWithSelectedPayoutAccount(selectedPayoutAccountItem);
        },
        [updateAccountingWithSelectedPayoutAccount, accountingPayoutAccountStore.selectablePayoutAccountDataIT2]
    );

    const onTabFromPayoutAccountField = useCallback(() => {
        if (
            accountingPayoutAccountStore.selectablePayoutAccountDataIT2 &&
            accountingPayoutAccountStore.selectablePayoutAccountDataIT2.length === 1
        ) {
            const onlyPayoutAccountItemInList = accountingPayoutAccountStore.selectablePayoutAccountDataIT2[0];
            updateAccountingWithSelectedPayoutAccount(onlyPayoutAccountItemInList);
        }

        accountingPayoutAccountStore.setTriggerFocusNextAccountingInput(true);
    }, [accountingPayoutAccountStore, updateAccountingWithSelectedPayoutAccount]);

    const { onKeyDownFunction, setActiveListItemIndex, NO_LIST_ITEM_SELECTED } = useSelectionListKeyHandler({
        listName: "accounting-payoutaccount",
        totalNumberOfDisplayedItems: invoiceStore.numberOfDisplayedItems,
        setIsListDisplayed: setDisplayPayoutAccountList,
        isListDisplayed: displayPayoutAccountList,
        onSelectItem: onSelectPayoutAccountItem,
        resetSearchQuery: resetSearchQuery,
        additionalTabActionWithNoItemSelected: onTabFromPayoutAccountField
    });

    const resetAmountOfItemsDisplayedInPayoutAccountList = useCallback(() => {
        setLastSrollYPositionPayoutAccountItems(0);
        invoiceStore.setNumberOfDisplayedItems(invoiceStore.sliceSizeOfDisplayedItems);
    }, [invoiceStore]);

    const onChangePayoutAccountInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            resetAmountOfItemsDisplayedInPayoutAccountList();

            const inputPayoutAccount = e.target.value;
            accountingPayoutAccountStore.setCurrentPayoutAccountSearchQueryIT2(inputPayoutAccount);
        },
        [accountingPayoutAccountStore, resetAmountOfItemsDisplayedInPayoutAccountList]
    );

    const onFocusPayoutAccountInput = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (invoiceStore.currentInvoice?.isPayoutAccountIdSelected && payoutAccountInputRef.current) {
                payoutAccountInputRef.current.select();
            }

            resetAmountOfItemsDisplayedInPayoutAccountList();
            setDisplayPayoutAccountList(true);
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
        },
        [
            resetAmountOfItemsDisplayedInPayoutAccountList,
            setActiveListItemIndex,
            NO_LIST_ITEM_SELECTED,
            invoiceStore.currentInvoice
        ]
    );

    const onClickPayoutAccountItem = useCallback(
        (payoutAccountData: IPayoutAccountData) => async (e: any) => {
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
            setDisplayPayoutAccountList(false);

            updateAccountingWithSelectedPayoutAccount(payoutAccountData);
        },
        [setActiveListItemIndex, NO_LIST_ITEM_SELECTED, updateAccountingWithSelectedPayoutAccount]
    );

    const onScrollPayoutAccountList = useCallback(
        (e: React.BaseSyntheticEvent) => {
            const windowHeight = e.target.clientHeight;
            const pixelsFromTop = e.target.scrollTop;
            const totalHeight = e.target.scrollHeight;

            const is150PixelsFromBottom = totalHeight - pixelsFromTop < windowHeight + 150;
            const isScrollingDown = pixelsFromTop > lastScrollPositionYPayoutAccountItems;

            const hasMoreResulsToDisplay =
                invoiceStore.numberOfDisplayedItems <
                accountingPayoutAccountStore.selectablePayoutAccountDataIT2.length;

            if (is150PixelsFromBottom && isScrollingDown && hasMoreResulsToDisplay) {
                invoiceStore.setNumberOfDisplayedItems(
                    invoiceStore.numberOfDisplayedItems + invoiceStore.sliceSizeOfDisplayedItems
                );
            }

            setLastSrollYPositionPayoutAccountItems(pixelsFromTop);
        },
        [lastScrollPositionYPayoutAccountItems, invoiceStore, accountingPayoutAccountStore]
    );

    const getSpanElementWithFormattedText = useCallback(
        (stringToFormat: string) => {
            const formattedString =
                accountingPayoutAccountStore.currentPayoutAccountSearchQueryIT2 !== ""
                    ? boldenMatchedSubstring(
                          accountingPayoutAccountStore.currentPayoutAccountSearchQueryIT2 ?? "",
                          stringToFormat,
                          "highlighted-text accounting-payoutaccount"
                      )
                    : stringToFormat;

            return (
                <span
                    className={`accounting-payoutaccount-text ${styles.AccountingAccountText}`}
                    dangerouslySetInnerHTML={{
                        __html: formattedString
                    }}
                />
            );
        },
        [accountingPayoutAccountStore.currentPayoutAccountSearchQueryIT2]
    );

    const payoutAccountList = (
        <ul
            id="accounting-payoutaccount-dropdown-list"
            className={`dropdown-list accounting-payoutaccount krediflow ${
                authStore.user?.erpType === ErpType.IT2 ? "IT2" : "RIMO"
            }`}
            onScroll={onScrollPayoutAccountList}
        >
            {accountingPayoutAccountStore.loadingPayoutAccountState === LOADING_PAYOUT_ACCOUNT_STATE.LOADING ? (
                <li className="d-flex align-items-center justify-content-center list-item accounting-payoutaccount loading">
                    <div>
                        <UI.RotatingSpinner noLogo size={20} />
                    </div>
                </li>
            ) : (
                accountingPayoutAccountStore.selectablePayoutAccountDataIT2
                    .slice(0, invoiceStore.numberOfDisplayedItems)
                    .map((payoutAccountData, index) => {
                        const formattedPayoutAccountNameSpan = payoutAccountData.name
                            ? getSpanElementWithFormattedText(payoutAccountData.name ?? "")
                            : undefined;

                        const formattedPayoutAccountIbanSpan = payoutAccountData.iban
                            ? getSpanElementWithFormattedText(payoutAccountData.iban ?? "")
                            : undefined;

                        return (
                            <li
                                key={index}
                                className="list-item accounting-payoutaccount"
                                onClick={onClickPayoutAccountItem(payoutAccountData)}
                                id={`accounting-payoutaccount-list-item-${index}`}
                            >
                                <div>
                                    {formattedPayoutAccountNameSpan}
                                    {payoutAccountData.default ? (
                                        <span style={{ marginLeft: 50 }}>{`[${t("labels.default")}]`}</span>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                                <div>{formattedPayoutAccountIbanSpan}</div>
                            </li>
                        );
                    })
            )}
        </ul>
    );

    const readOnlyPayoutAccountFieldIT2 = (
        <div className={invoiceStore.currentInvoice?.canSeePayoutAccountField() ? "" : "d-none"}>
            <div className="bold-text">
                {accountingPayoutAccountStore.selectedPayoutAccountQueryStringIT2
                    ? `${t("screens.kredi_flow.accountings.payout_account")}:`
                    : ""}
            </div>
            <div>
                {accountingPayoutAccountStore.selectedPayoutAccountQueryStringIT2
                    ? friendlyFormatIBAN(accountingPayoutAccountStore.selectedPayoutAccountQueryStringIT2)
                    : ""}
            </div>
        </div>
    );

    return (
        <>
            {invoiceStore.isEditing ? (
                <>
                    <UI.Input
                        ref={payoutAccountInputRef}
                        autoComplete="off"
                        label={`${t("screens.kredi_flow.accountings.payout_account").toString()}*`}
                        type="text"
                        value={accountingPayoutAccountStore.currentPayoutAccountSearchQueryIT2 ?? ""}
                        onChange={onChangePayoutAccountInput}
                        onFocus={onFocusPayoutAccountInput}
                        onKeyDown={onKeyDownFunction}
                        disabled={accountingPayoutAccountStore.selectablePayoutAccountDataIT2.length === 1}
                    />
                    {displayPayoutAccountList ? payoutAccountList : undefined}
                </>
            ) : (
                readOnlyPayoutAccountFieldIT2
            )}
        </>
    );
};

export const PayoutAccountIT2Field = Sentry.withProfiler(observer(PayoutAccountIT2FieldBase));
